import * as React from 'react';
import { filter, get, has, isEmpty, indexOf, keys, uniq } from 'lodash';

import { IMemberProgram, IMemberProgramMap, TProgram } from 'src/common/models/program';
import { IResource } from 'src/common/models/resource';
import { FetchContextProvider } from 'src/utils/context/FetchContext';
import { ISocialAccount } from 'src/common/models/socialAccount';

import { uniqueMemberPrograms } from '../utils';

const { createContext, useEffect, useState } = React;

interface IResourceContext {
  resourceId: IResource['id'];
  onRequestAddEmail?(type: TEmailType);
}

type TEmailType = 'gmail' | 'outlook';

export interface IInviteContext {
  apiEndpoint: string;
  clientId: string;
  userId: string;
  resourceContext: IResourceContext;
  memberPrograms: IMemberProgramMap;
  selectedProgramId: TProgram['id'];
  fetchingSocialAccountIds: number[];
  updateResourceContext: React.Dispatch<React.SetStateAction<IResourceContext>>;
  updateMemberPrograms: (socialAccountId: number, programs: IMemberProgram[]) => void;
  updateSelectedProgramId: React.Dispatch<React.SetStateAction<TProgram['id']>>;
  updateFetchingSocialAccountIds: (...socialAccountIds: number[]) => void;
  updatedSelectedSocialAccounts: (socialAccounts: ISocialAccount[]) => void;
  isWorkflowEnabled?: boolean;
  selectedSocialAccounts: ISocialAccount[];
}

export const InviteContext = createContext<IInviteContext>(null);

interface IProps {
  apiEndpoint?: string;
  clientId: string;
  userId: string;
  onRequestAddEmail?(type: TEmailType);
  isWorkflowEnabled?: boolean;
  initialSelectedProjectId?: TProgram['id'];
}

export const InviteContextProvider: React.FC<IProps> = (props) => {
  const [resourceContext, setResourceContext] = useState<IResourceContext>({
    resourceId: null,
    onRequestAddEmail: props.onRequestAddEmail,
  });
  const [fetchingSocialAccountIds, setFetchingSocialAccountIds] = useState<number[]>([]);
  const [memberPrograms, setMemberPrograms] = useState<IMemberProgramMap>({});
  const [selectedProgramId, setSelectedProgramId] = useState<TProgram['id']>();
  const [selectedSocialAccounts, setSelectedSocialAccounts] = useState<ISocialAccount[]>([]);

  // Remove account IDs in `fetchingSocialAccountIds` when `memberPrograms` is updated
  const socialAccountIds = keys(memberPrograms).sort().join();
  useEffect(() => {
    if (!isEmpty(fetchingSocialAccountIds) && !isEmpty(memberPrograms)) {
      setFetchingSocialAccountIds((accountIds) => filter(
        accountIds,
        (accountId) => !has(memberPrograms, accountId),
      ));
    }
  }, [socialAccountIds, memberPrograms]);

  useEffect(() => (
    props.initialSelectedProjectId && setSelectedProgramId(props.initialSelectedProjectId)
  ), [props.initialSelectedProjectId]);

  const value: IInviteContext = {
    apiEndpoint: props.apiEndpoint || '/api',
    clientId: props.clientId,
    userId: props.userId,
    isWorkflowEnabled: props.isWorkflowEnabled,
    resourceContext,
    memberPrograms,
    selectedProgramId,
    fetchingSocialAccountIds,
    selectedSocialAccounts,
    updateResourceContext: setResourceContext,
    updateMemberPrograms: (socialAccountId: number, programs: IMemberProgram[]) => {
      setMemberPrograms((currentMemberPrograms) => ({
        ...currentMemberPrograms,
        [socialAccountId]: uniqueMemberPrograms(
          ...programs,
          ...get(currentMemberPrograms, socialAccountId, []),
        ),
      }));
    },
    updatedSelectedSocialAccounts: (socialAccounts: ISocialAccount[]) => {
      setSelectedSocialAccounts(socialAccounts);
    },
    updateFetchingSocialAccountIds: (...socialAccountIds: number[]) => {
      if (socialAccountIds.length > 0) {
        const filteredAccountIds = filter(socialAccountIds, (socialAccountId) => (
          !has(memberPrograms, socialAccountId) &&
          indexOf(fetchingSocialAccountIds, socialAccountId) < 0
        ));
        if (filteredAccountIds.length > 0) {
          setFetchingSocialAccountIds((accountIds) => uniq([...accountIds, ...filteredAccountIds]));
        }
      }
    },
    updateSelectedProgramId: setSelectedProgramId,
  };

  return (
    <FetchContextProvider>
      <InviteContext.Provider value={value}>
        {props.children}
      </InviteContext.Provider>
    </FetchContextProvider>
  );
};
